import { AlertTriangle, ShieldAlert } from 'lucide-react';

import { channelLabel, renderTemplateText, META_CATEGORY_META } from './proactive-adapter';

import type { SelectedPerson } from './Step1PessoaCanal';
import type { MetaCategory } from './types';
import type { GuardianshipIndicator } from '../persons/persons-adapter';
import type { ChannelKind } from '@lume/shared';

interface Step4Props {
  person: SelectedPerson;
  channel: ChannelKind;
  category: MetaCategory;
  templateName: string;
  bodyPreview: string | undefined;
  vars: Record<string, string>;
  operatorName: string;
  operatorRole: string;
  /** Derivado de getPerson().guardianshipsAsWard (reuso Tela 2). Honest. */
  guardianship: GuardianshipIndicator | null;
  /** Mensagem honesta de bloqueio (422 do POST), quando houver. */
  blockedMessage: string | null;
}

/**
 * Passo 4 — Confirmar. Resumo + preview de como o cliente recebe (substituição
 * client-side, espelha o render do backend) + aviso de guardianship derivado do
 * dado real (não fabricado). D2/D3/D4: sem Agendar / Notificar supervisor /
 * dual-approval (não existem no v1 — affordance falsa). O bloqueio honesto vem
 * do 422 do POST (D8), nunca pré-computado.
 */
export function Step4Confirmar({
  person,
  channel,
  category,
  templateName,
  bodyPreview,
  vars,
  operatorName,
  operatorRole,
  guardianship,
  blockedMessage,
}: Step4Props): JSX.Element {
  const meta = META_CATEGORY_META[category];
  const previewText = renderTemplateText(bodyPreview, vars);
  const showGuard = guardianship !== null && guardianship.state !== 'none';

  return (
    <>
      <div className="coleta-section">
        <span className="coleta-section__label">Resumo</span>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '120px 1fr',
            gap: 'var(--space-2) var(--space-3)',
            fontSize: 'var(--text-xs)',
          }}
        >
          <span className="muted mono">Para</span>
          <span>
            <strong>{person.displayName}</strong>
          </span>
          <span className="muted mono">Canal</span>
          <span>{channelLabel(channel)}</span>
          <span className="muted mono">Categoria</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {meta.label}
            <span className="mono" style={{ color: 'var(--fg-3)' }}>
              {meta.cost === '—' ? 'sem custo de template' : `custo ${meta.cost}`}
            </span>
          </span>
          <span className="muted mono">Template</span>
          <span className="mono">{templateName}</span>
          <span className="muted mono">Operador</span>
          <span>
            {operatorName} ({operatorRole})
          </span>
        </div>
      </div>

      <div className="coleta-section">
        <span className="coleta-section__label">Preview · como o cliente vai receber</span>
        <div
          style={{
            background: 'var(--bg-canvas)',
            padding: 'var(--space-4)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <div className="preview-bubble">
            {previewText !== '' ? previewText : '—'}
            <span className="preview-bubble__time">{operatorName.split(' ')[0]}</span>
          </div>
          <div
            className="muted mono"
            style={{ fontSize: 10, marginTop: 'var(--space-2)', textAlign: 'center' }}
          >
            ↑ aparência simulada da mensagem no canal do cliente
          </div>
        </div>
      </div>

      {showGuard && guardianship !== null && (
        <div
          className="form-row__note"
          style={{
            color: 'var(--warning-fg)',
            background: 'var(--warning-bg)',
            borderColor: 'var(--warning-border)',
          }}
        >
          <AlertTriangle size={13} />
          <span>
            <strong>Atenção · guardianship:</strong> esta pessoa tem {guardianship.label}. Considere
            o responsável legal antes de iniciar o contato.
          </span>
        </div>
      )}

      {blockedMessage !== null && (
        <div className="proactive-blocked" role="alert">
          <ShieldAlert size={15} />
          <span>{blockedMessage}</span>
        </div>
      )}
    </>
  );
}
